import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Users, BookOpen, Headphones, Settings, 
  LogOut, Menu, X, Search, Plus, Edit, Trash2, 
  Eye, EyeOff, Filter, Download, Upload, AlertCircle,
  CheckCircle, Loader, ChevronDown, Calendar, DollarSign,
  TrendingUp, Database, Shield, Bell, FileText, 
  CreditCard, RefreshCw, BarChart, PieChart, Globe,
  MessageSquare, UserCheck, Clock, Star, Award,
  Zap, Target, Shield as ShieldIcon, Cpu, Server,
  Network, HardDrive, Activity, Terminal, Database as DatabaseIcon,
  CheckSquare, Square, ExternalLink, MoreVertical, Key, Mail
} from 'lucide-react';

// Import your mock data
import { courses as mockCourses, podcasts as mockPodcasts } from '../data/mockData';
import { API_BASE } from "../config"

const adminApi = {
  // Authentication
  login: async (credentials) => {
    const response = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });
    return response.json();
  },

  // Courses
  getCourses: async (token) => {
    try {
      const response = await fetch(`${API_BASE}/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch courses');
      return await response.json();
    } catch (error) {
      console.error('Using mock courses due to API error:', error);
      return { success: true, courses: mockCourses, count: mockCourses.length };
    }
  },

  createCourse: async (courseData, token) => {
    const response = await fetch(`${API_BASE}/courses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(courseData)
    });
    return response.json();
  },

  updateCourse: async (id, updates, token) => {
    const response = await fetch(`${API_BASE}/courses/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });
    return response.json();
  },

  deleteCourse: async (id, token) => {
    const response = await fetch(`${API_BASE}/courses/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.json();
  },

  togglePublishCourse: async (id, token) => {
    const response = await fetch(`${API_BASE}/courses/${id}/toggle-publish`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.json();
  },

  // Podcasts
  getPodcasts: async (token) => {
    try {
      const response = await fetch(`${API_BASE}/podcasts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch podcasts');
      return await response.json();
    } catch (error) {
      console.error('Using mock podcasts due to API error:', error);
      return { success: true, podcasts: mockPodcasts, count: mockPodcasts.length };
    }
  },

  createPodcast: async (podcastData, token) => {
    const response = await fetch(`${API_BASE}/podcasts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(podcastData)
    });
    return response.json();
  },

  uploadPodcastAudio: async (audioFile, token) => {
    const formData = new FormData();
    formData.append('audio', audioFile);
    
    const response = await fetch(`${API_BASE}/podcasts/upload-audio`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return response.json();
  },

  scanAudioFiles: async (token) => {
    const response = await fetch(`${API_BASE}/podcasts/scan-audio`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.json();
  },

  // Bulk import
  bulkImportCourses: async (courses, token) => {
    const response = await fetch(`${API_BASE}/courses/bulk-import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ courses })
    });
    return response.json();
  },

  bulkImportPodcasts: async (podcasts, token) => {
    const response = await fetch(`${API_BASE}/podcasts/bulk-import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ podcasts })
    });
    return response.json();
  },

// Stats
getStats: async (token) => {
  try {
    const response = await fetch(`${API_BASE}/stats.php`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch stats');
    return await response.json();
  } catch (error) {
    console.error('Using mock stats due to API error:', error);
    // Return mock stats
    return {
      success: true,
      total_users: 12500,
      pro_users: 3200,
      pro_percentage: 26,
      new_users_30d: 850,
      monthly_revenue: 125000,
      revenue_growth: 12.5,
      total_courses: mockCourses.length,
      new_courses_30d: 3,
      total_podcasts: mockPodcasts.length,
      open_tickets: 24,
      tiers: {
        free_users: 6200,
        plus_users: 3100,
        pro_users: 3200
      }
    };
  }
},
  getDatabaseStatus: async (token) => {
    try {
      const response = await fetch(`${API_BASE}/database-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch database status');
      return await response.json();
    } catch (error) {
      console.error('Using mock database status:', error);
      return {
        success: true,
        saints_admin: {
          status: 'connected',
          records: 12500,
          lastChecked: new Date().toISOString()
        },
        saints_content: {
          status: 'connected',
          records: mockCourses.length + mockPodcasts.length,
          lastChecked: new Date().toISOString()
        },
        users_login: {
          status: 'connected',
          records: 12500,
          lastChecked: new Date().toISOString()
        }
      };
    }
  },

  // Categories
  getCategories: async (token) => {
    try {
      const response = await fetch(`${API_BASE}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return await response.json();
    } catch (error) {
      console.error('Error fetching categories:', error);
      const courseCats = [...new Set(mockCourses.map(c => c.category))];
      const podcastTopics = [...new Set(mockPodcasts.map(p => p.topic))];
      return {
        success: true,
        course_categories: courseCats,
        podcast_topics: podcastTopics,
        all_categories: [...new Set([...courseCats, ...podcastTopics])]
      };
    }
  }
};

// Helper functions for tier mapping
const mapCourseTierForDisplay = (dbTier) => {
  switch(dbTier) {
    case 'scholarship': return 'Pro';
    case 'premium': return 'Plus';
    case 'free': return 'Free';
    default: return dbTier?.charAt(0)?.toUpperCase() + dbTier?.slice(1) || 'Free';
  }
};

const getCourseTierColor = (dbTier) => {
  switch(dbTier) {
    case 'scholarship':
      return 'bg-purple-100 text-purple-800';
    case 'premium':
      return 'bg-green-100 text-green-800';
    case 'free':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getPodcastTierColor = (tier) => {
  switch(tier) {
    case 'pro':
      return 'bg-purple-100 text-purple-800';
    case 'plus':
      return 'bg-green-100 text-green-800';
    case 'free':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getLevelColor = (level) => {
  switch(level) {
    case 'Beginner': return 'bg-green-100 text-green-800';
    case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
    case 'Advanced': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

function AdminDashboard() {
  const isLocal = window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1';
  const API_BASE = isLocal ? '${API_BASE}' : '/api/v2/admin';

  
  const [activeTab, setActiveTab] = useState('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [databaseStatus, setDatabaseStatus] = useState(null);
  const [categories, setCategories] = useState([]);

  const adminToken = localStorage.getItem('adminToken');

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      if (!adminToken) {
        // Use mock data if no token
        setStats(await adminApi.getStats(null));
        setCourses(mockCourses);
        setPodcasts(mockPodcasts);
        setDatabaseStatus(await adminApi.getDatabaseStatus(null));
        setCategories(await adminApi.getCategories(null));
      } else {
        // Fetch from API with token
        setStats(await adminApi.getStats(adminToken));
        
        if (activeTab === 'courses') {
          const coursesData = await adminApi.getCourses(adminToken);
          setCourses(coursesData.courses || []);
        }
        
        if (activeTab === 'podcasts') {
          const podcastsData = await adminApi.getPodcasts(adminToken);
          setPodcasts(podcastsData.podcasts || []);
        }

        setDatabaseStatus(await adminApi.getDatabaseStatus(adminToken));
        setCategories(await adminApi.getCategories(adminToken));
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, [activeTab, adminToken]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.location.href = '/admin/login';
  };

  const handleRefresh = () => {
    fetchAdminData();
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
     { id: 'users', label: 'Users', icon: Users }, // <-- ADD THIS LINE
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'podcasts', label: 'Podcasts', icon: Headphones },
    { id: 'email-management', label: 'Email', icon: Mail },
     { id: 'ambassador-management', label: 'Ambassadors', icon: Award },
    { id: 'bulk-import', label: 'Bulk Import', icon: Upload },
    { id: 'system-health', label: 'System Health', icon: Server },
  ];

  const renderTabContent = () => {
    if (loading && activeTab === 'overview') {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-2 text-gray-600">Loading dashboard data...</span>
        </div>
      );
    }

    switch (activeTab) {
  case 'overview':
    return <OverviewTab stats={stats} databaseStatus={databaseStatus} onRefresh={handleRefresh} />;
  case 'courses':
    return <CourseManagementTab courses={courses} loading={loading} onRefresh={handleRefresh} categories={categories} />;
  case 'podcasts':
    return <PodcastManagementTab podcasts={podcasts} loading={loading} onRefresh={handleRefresh} categories={categories} />;
  case 'users': // <-- ADD THIS CASE
    return <UserManagementTab />;
     case 'email-management': // <-- ADD THIS CASE
      return <EmailManagementTab />;
  case 'ambassador-management': // <-- ADD THIS CASE
    return <AmbassadorManagementTab />;
      case 'bulk-import':
    return <BulkImportTab token={adminToken} onImportComplete={fetchAdminData} />;
  case 'system-health':
    return <SystemHealthTab databaseStatus={databaseStatus} onRefresh={handleRefresh} />;
  default:
    return <OverviewTab stats={stats} databaseStatus={databaseStatus} onRefresh={handleRefresh} />;
}
  };
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div 
            className="fixed inset-0 bg-gray-900 bg-opacity-75" 
            onClick={() => setIsSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 max-w-xs w-full bg-gray-900 text-white shadow-xl">
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <ShieldIcon className="w-8 h-8 text-primary" />
                    <div>
                      <h1 className="text-xl font-bold">Saints Academy</h1>
                      <p className="text-sm text-gray-400">Admin Dashboard</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setIsSidebarOpen(false);
                      }}
                      className={`
                        w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors
                        ${activeTab === tab.id 
                          ? 'bg-primary text-white' 
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="p-3 border-t border-gray-800">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">Admin User</p>
                    <p className="text-xs text-gray-400 truncate">Super Admin</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex lg:flex-shrink-0">
          <div className="w-64 flex flex-col bg-gray-900 text-white">
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center space-x-3">
                <ShieldIcon className="w-8 h-8 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">Saints Academy</h1>
                  <p className="text-sm text-gray-400">Admin Dashboard</p>
                </div>
              </div>
            </div>

            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors
                      ${activeTab === tab.id 
                        ? 'bg-primary text-white' 
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{tab.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="p-3 border-t border-gray-800">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                  <UserCheck className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">Admin User</p>
                  <p className="text-xs text-gray-400 truncate">Super Admin</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen">
          {/* Header */}
          <header className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
            <div className="px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setIsSidebarOpen(true)}
                    className="lg:hidden -ml-2 mr-2 p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                  
                  <div className="hidden sm:block">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {tabs.find(t => t.id === activeTab)?.label || 'Dashboard'}
                    </h2>
                    <p className="text-sm text-gray-600">Manage your admin panel</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="hidden md:block relative">
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search..."
                      className="pl-10 pr-4 py-2 w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <button
                    onClick={handleRefresh}
                    className="p-2 rounded-lg hover:bg-gray-100"
                    title="Refresh data"
                  >
                    <RefreshCw className="w-5 h-5 text-gray-600" />
                  </button>
                  
                  <button className="p-2 rounded-lg hover:bg-gray-100 relative">
                    <Bell className="w-5 h-5 text-gray-600" />
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  </button>
                  
                  <div className="hidden lg:flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-medium text-sm">
                      A
                    </div>
                    <div className="text-sm">
                      <div className="font-medium">Admin</div>
                      <div className="text-gray-500">Super Admin</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile search */}
              <div className="mt-3 md:hidden">
                <div className="relative">
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1">
            <div className="px-4 sm:px-6 lg:px-8 py-6">
              <div className="max-w-7xl mx-auto">
                {renderTabContent()}
              </div>
            </div>
          </main>

          {/* Footer */}
          <footer className="border-t border-gray-200 bg-white px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-sm text-gray-600 mb-2 md:mb-0">
                © 2026 Saints Academy. All rights reserved.
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <Activity className="w-4 h-4 mr-1 text-green-500" />
                  System Status: <span className="ml-1 font-medium text-green-600">Operational</span>
                </span>
                <span>Version 2.0.0</span>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component - MOVE THIS ABOVE OverviewTab
function StatCard({ title, value, change, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">{title}</h4>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="text-3xl font-bold text-gray-900 mb-2">{value}</div>
      <p className="text-sm text-gray-600">{change}</p>
    </div>
  );
}

// Database Status Card Component
function DatabaseStatusCard({ name, status }) {
  const isConnected = status?.status === 'connected';
  
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-medium text-gray-900">{name}</span>
        <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
      </div>
      <div className="text-sm text-gray-600 mb-1">
        Status: <span className={`font-medium ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
          {status?.status || 'unknown'}
        </span>
      </div>
      <div className="text-sm text-gray-600 mb-1">
        Records: <span className="font-medium">{status?.records?.toLocaleString() || '0'}</span>
      </div>
      <div className="text-xs text-gray-500">
        Last checked: {status?.lastChecked ? new Date(status.lastChecked).toLocaleTimeString() : 'N/A'}
      </div>
    </div>
  );
}

// Quick Action Card Component
function QuickActionCard({ title, description, icon: Icon, color, onClick }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 hover:bg-blue-200',
    green: 'bg-green-100 text-green-600 hover:bg-green-200',
    orange: 'bg-orange-100 text-orange-600 hover:bg-orange-200',
    purple: 'bg-purple-100 text-purple-600 hover:bg-purple-200'
  };

  return (
    <button 
      onClick={onClick}
      className={`${colorClasses[color]} rounded-lg p-4 text-left transition-colors`}
    >
      <Icon className="w-8 h-8 mb-3" />
      <div className="font-medium mb-1">{title}</div>
      <div className="text-sm opacity-80">{description}</div>
    </button>
  );
}

// OverviewTab Component - PUT THIS AFTER the helper components
function OverviewTab({ stats, databaseStatus, onRefresh }) {
  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading overview data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Dashboard Overview</h3>
          <p className="text-sm text-gray-600">Welcome back, Admin. Here's what's happening today.</p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Users" 
          value={stats.total_users?.toLocaleString() || '0'} 
          change={`${stats.new_users_growth ? `${stats.new_users_growth > 0 ? '+' : ''}${stats.new_users_growth}%` : '0%'} this month`}
          icon={Users}
          color="blue"
        />
        <StatCard 
          title="Pro Users" 
          value={stats.pro_users?.toLocaleString() || '0'} 
          change={`${stats.pro_percentage || 0}% of total`}
          icon={UserCheck}
          color="purple"
        />
        
        {/* Monthly Revenue Card */}
        <StatCard 
          title="Monthly Revenue" 
          value={stats.monthly_revenue > 0 ? `R${stats.monthly_revenue?.toLocaleString()}` : 'R0'} 
          change={stats.monthly_revenue > 0 ? `${stats.revenue_growth || 0}% growth` : 'Coming soon'}
          icon={DollarSign}
          color="green"
        />
        
        <StatCard 
          title="New Users (30d)" 
          value={stats.new_users_30d?.toLocaleString() || '0'} 
          change={stats.new_users_growth ? `${stats.new_users_growth > 0 ? '+' : ''}${stats.new_users_growth}% from last month` : 'No previous data'}
          icon={TrendingUp}
          color="orange"
        />
      </div>

      {/* Content Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Course Library</h4>
            <BookOpen className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-2">
            {stats.total_courses || 0}
          </div>
          <p className="text-sm text-gray-600">
            {stats.new_courses_30d || 0} new in last 30 days
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Podcast Library</h4>
            <Headphones className="w-5 h-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-2">
            {stats.total_podcasts || 0}
          </div>
          <p className="text-sm text-gray-600">
            {stats.total_podcasts || 0} total episodes
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Support Tickets</h4>
            <MessageSquare className="w-5 h-5 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-2">
            {stats.open_tickets || 0}
          </div>
          <p className="text-sm text-gray-600">
            {stats.open_tickets || 0} open tickets
          </p>
        </div>
      </div>

      {/* Database Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="font-medium text-gray-900">Database Status</h4>
            <p className="text-sm text-gray-600">Connection status across all databases</p>
          </div>
          <DatabaseIcon className="w-5 h-5 text-gray-400" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {databaseStatus ? (
            <>
              <DatabaseStatusCard 
                name="saints_admin" 
                status={databaseStatus.saints_admin} 
              />
              <DatabaseStatusCard 
                name="saints_content" 
                status={databaseStatus.saints_content} 
              />
              <DatabaseStatusCard 
                name="users_login" 
                status={databaseStatus.users_login} 
              />
            </>
          ) : (
            <div className="col-span-3 flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-gray-600">Loading database status...</span>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-4">Quick Actions</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <QuickActionCard 
            title="Add New Course" 
            description="Create a new course" 
            icon={BookOpen}
            color="blue"
            onClick={() => window.location.href = '#courses'}
          />
          <QuickActionCard 
            title="Upload Podcast" 
            description="Add a new podcast episode" 
            icon={Headphones}
            color="green"
            onClick={() => window.location.href = '#podcasts'}
          />
          <QuickActionCard 
            title="Bulk Import" 
            description="Import mock data" 
            icon={Upload}
            color="purple"
            onClick={() => window.location.href = '#bulk-import'}
          />
          <QuickActionCard 
            title="System Health" 
            description="Monitor system status" 
            icon={Server}
            color="orange"
            onClick={() => window.location.href = '#system-health'}
          />
        </div>
      </div>
    </div>
  );
}

// UserManagementTab Component - Add this to your AdminDashboard.jsx
function UserManagementTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const adminToken = localStorage.getItem('adminToken');

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        tier: filterTier !== 'all' ? filterTier : '',
        role: filterRole !== 'all' ? filterRole : ''
      });

      const response = await fetch(`${API_BASE}/users?${params}`, 
        {
          headers: { 
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin/login';
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        setUsers(data.users || []);
      } else {
        setError(data.error || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to fetch users. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, filterRole, filterTier]);

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      alert('Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
  
const response = await fetch(`${API_BASE}/users/${selectedUser.id}/reset-password`, 
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          },
          body: JSON.stringify({ newPassword })
        }
      );

      const data = await response.json();
      
      if (data.success) {
        alert(`✅ Password reset successful for ${selectedUser.email}`);
        setShowPasswordModal(false);
        setSelectedUser(null);
        setNewPassword('');
      } else {
        alert(`❌ Failed to reset password: ${data.error}`);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      alert('❌ Failed to reset password.');
    }
  };

  const handleUpdateUser = async (userId, field, value) => {
    try {
        const response = await fetch(`${API_BASE}/users/${userId}`, 
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          },
          body: JSON.stringify({ [field]: value })
        }
      );

      const data = await response.json();
      
      if (data.success) {
        alert(`✅ User updated successfully`);
        fetchUsers();
      } else {
        alert(`❌ Failed to update user: ${data.error}`);
      }
    } catch (error) {
      console.error('Update user error:', error);
      alert('❌ Failed to update user.');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!confirm(`Are you sure you want to delete user ${user.email}?\n\nThis will deactivate their account.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/users/${user.id}`, 
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      const data = await response.json();
      
      if (data.success) {
        alert(`✅ User deleted successfully`);
        fetchUsers();
      } else {
        alert(`❌ Failed to delete user: ${data.error}`);
      }
    } catch (error) {
      console.error('Delete user error:', error);
      alert('❌ Failed to delete user.');
    }
  };

  const handleToggleActive = async (user) => {
    const newStatus = !user.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!confirm(`Are you sure you want to ${action} user ${user.email}?`)) {
      return;
    }

    await handleUpdateUser(user.id, 'is_active', newStatus ? 1 : 0);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">User Management</h3>
          <p className="text-sm text-gray-600">
            Manage user accounts, subscriptions, and permissions
          </p>
        </div>
        <button 
          onClick={() => fetchUsers()}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="instructor">Instructor</option>
            </select>
            
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Tiers</option>
              <option value="free">Free</option>
              <option value="plus">Plus</option>
              <option value="pro">Pro</option>
              <option value="scholarship">Scholarship</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.name || 'No Name'}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">{user.email}</div>
                          <div className="text-xs text-gray-400">ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={user.role || 'user'}
                        onChange={(e) => handleUpdateUser(user.id, 'role', e.target.value)}
                        className={`px-2 py-1 text-xs rounded border ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                          user.role === 'instructor' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                          'bg-gray-100 text-gray-800 border-gray-300'
                        }`}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="instructor">Instructor</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-1">
                        <select
                          value={user.access_tier || 'free'}
                          onChange={(e) => handleUpdateUser(user.id, 'access_tier', e.target.value)}
                          className={`px-2 py-1 text-xs rounded border ${
                            user.access_tier === 'pro' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                            user.access_tier === 'plus' ? 'bg-green-100 text-green-800 border-green-300' :
                            user.access_tier === 'scholarship' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                            'bg-gray-100 text-gray-800 border-gray-300'
                          }`}
                        >
                          <option value="free">Free</option>
                          <option value="plus">Plus</option>
                          <option value="pro">Pro</option>
                          <option value="scholarship">Scholarship</option>
                        </select>
                        <div className="text-xs text-gray-600">
                          {user.subscription_status || 'No subscription'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`px-2 py-1 text-xs rounded-full ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium">{user.enrolled_courses || 0} courses</div>
                        <div className="text-gray-500">{user.completed_lessons || 0} completed</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(user.created_at).toLocaleDateString()}
                      <div className="text-xs text-gray-500">
                        {user.last_login 
                          ? `Last: ${new Date(user.last_login).toLocaleDateString()}`
                          : 'Never logged in'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 space-x-2">
                      <button 
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordModal(true);
                        }}
                        className="text-orange-600 hover:text-orange-800 p-1"
                        title="Reset password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => {
                          const newEmail = prompt('Enter new email:', user.email);
                          if (newEmail && newEmail !== user.email) {
                            handleUpdateUser(user.id, 'email', newEmail);
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit email"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No users found</p>
            <p className="text-sm mt-1">
              {searchQuery || filterRole !== 'all' || filterTier !== 'all'
                ? 'Try adjusting your search criteria'
                : 'No users in the system yet'
              }
            </p>
            <button 
              onClick={() => {
                setSearchQuery('');
                setFilterRole('all');
                setFilterTier('all');
              }}
              className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
                <button 
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Reset password for <span className="font-medium">{selectedUser.email}</span>
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      User will be required to use this new password on next login
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={!newPassword || newPassword.length < 8}
                  className={`px-4 py-2 rounded-lg ${
                    !newPassword || newPassword.length < 8
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// CourseManagementTab Component
function CourseManagementTab({ courses, loading, onRefresh, categories }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    skillLevel: 'Beginner',
    duration: '',
    price: '',
    tier: 'free',
    category: '',
    topics: [],
    hasCertificate: false,
    thumbnail_url: '',
    objectives: []
  });

  // Calculate total lessons
  const getTotalLessons = (course) => {
    return course.lesson_count || 0;
  };

  // Filter and sort courses
  const filteredCourses = courses?.filter(course => {
    const matchesSearch = course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTier = filterTier === 'all' || 
                       (filterTier === 'free' && course.tier === 'free') ||
                       (filterTier === 'plus' && course.tier === 'premium') ||
                       (filterTier === 'pro' && course.tier === 'scholarship');
    
    const matchesCategory = filterCategory === 'all' || course.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'published' && course.is_published) ||
                         (filterStatus === 'draft' && !course.is_published);
    
    const matchesLevel = filterLevel === 'all' || course.skillLevel === filterLevel;

    return matchesSearch && matchesTier && matchesCategory && matchesStatus && matchesLevel;
  }) || [];

  // Sort courses
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    switch(sortBy) {
      case 'newest':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'oldest':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case 'students_high':
        return (b.students || 0) - (a.students || 0);
      case 'students_low':
        return (a.students || 0) - (b.students || 0);
      case 'rating_high':
        return (b.rating || 0) - (a.rating || 0);
      case 'rating_low':
        return (a.rating || 0) - (b.rating || 0);
      default:
        return 0;
    }
  });

  // Bulk actions
  const handleSelectAll = () => {
    if (selectedCourses.length === sortedCourses.length) {
      setSelectedCourses([]);
    } else {
      setSelectedCourses(sortedCourses.map(c => c.id));
    }
  };

  const handleSelectCourse = (courseId) => {
    setSelectedCourses(prev => 
      prev.includes(courseId) 
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const handleBulkPublish = () => {
    if (selectedCourses.length === 0) return;
    const action = confirm(`Publish ${selectedCourses.length} selected courses?`);
    if (action) {
      alert(`Bulk publish ${selectedCourses.length} courses - To be implemented with backend API`);
      setSelectedCourses([]);
    }
  };

  const handleBulkUnpublish = () => {
    if (selectedCourses.length === 0) return;
    const action = confirm(`Unpublish ${selectedCourses.length} selected courses?`);
    if (action) {
      alert(`Bulk unpublish ${selectedCourses.length} courses - To be implemented with backend API`);
      setSelectedCourses([]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedCourses.length === 0) return;
    const action = confirm(`Delete ${selectedCourses.length} selected courses? This action cannot be undone.`);
    if (action) {
      alert(`Bulk delete ${selectedCourses.length} courses - To be implemented with backend API`);
      setSelectedCourses([]);
    }
  };

  // Course actions
  const handleCreateCourse = async () => {
    if (!newCourse.title || !newCourse.description) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      alert('Course created successfully! (API integration needed)');
      setIsCreating(false);
      setNewCourse({
        title: '',
        description: '',
        skillLevel: 'Beginner',
        duration: '',
        price: '',
        tier: 'free',
        category: '',
        topics: [],
        hasCertificate: false,
        thumbnail_url: '',
        objectives: []
      });
      onRefresh();
    } catch (error) {
      alert('Failed to create course: ' + error.message);
    }
  };

  const handleEditCourse = (course) => {
    alert(`Edit course: ${course.title} - Backend API endpoint needed`);
  };

  const handleDeleteCourse = (course) => {
    if (confirm(`Are you sure you want to delete "${course.title}"? This action cannot be undone.`)) {
      alert(`Delete course: ${course.title} - Backend API endpoint needed`);
    }
  };

  const handleTogglePublish = (course) => {
    const action = !course.is_published ? 'publish' : 'unpublish';
    if (confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} course "${course.title}"?`)) {
      alert(`${action} course: ${course.title} - Backend API endpoint needed`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading courses...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Course Management</h3>
          <p className="text-sm text-gray-600">
            {courses?.length || 0} total courses • {filteredCourses.length} filtered
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={onRefresh}
            className="p-2 rounded-lg hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setIsCreating(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            <span>Add Course</span>
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedCourses.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">
                {selectedCourses.length} course{selectedCourses.length > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkPublish}
                className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
              >
                Publish
              </button>
              <button
                onClick={handleBulkUnpublish}
                className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-sm"
              >
                Unpublish
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedCourses([])}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search courses by title, description, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Tiers</option>
              <option value="free">Free</option>
              <option value="plus">Plus</option>
              <option value="pro">Pro</option>
            </select>
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Categories</option>
              {categories?.course_categories?.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Levels</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="students_high">Most Students</option>
              <option value="students_low">Fewest Students</option>
              <option value="rating_high">Highest Rating</option>
              <option value="rating_low">Lowest Rating</option>
            </select>
          </div>
        </div>
      </div>

      {/* Create Course Modal */}
      {isCreating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Create New Course</h3>
                <button 
                  onClick={() => setIsCreating(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course Title *
                  </label>
                  <input
                    type="text"
                    value={newCourse.title}
                    onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter course title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={newCourse.description}
                    onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter course description"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Skill Level
                    </label>
                    <select
                      value={newCourse.skillLevel}
                      onChange={(e) => setNewCourse({...newCourse, skillLevel: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={newCourse.duration}
                      onChange={(e) => setNewCourse({...newCourse, duration: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="e.g., 6 hours"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tier
                    </label>
                    <select
                      value={newCourse.tier}
                      onChange={(e) => setNewCourse({...newCourse, tier: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="free">Free</option>
                      <option value="plus">Plus</option>
                      <option value="pro">Pro</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price Display
                    </label>
                    <input
                      type="text"
                      value={newCourse.price}
                      onChange={(e) => setNewCourse({...newCourse, price: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="e.g., Free, Saints Plus, Saints Pro"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={newCourse.category}
                    onChange={(e) => setNewCourse({...newCourse, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="e.g., Technology & AI"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thumbnail URL
                  </label>
                  <input
                    type="text"
                    value={newCourse.thumbnail_url}
                    onChange={(e) => setNewCourse({...newCourse, thumbnail_url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="https://images.unsplash.com/photo-..."
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasCertificate"
                    checked={newCourse.hasCertificate}
                    onChange={(e) => setNewCourse({...newCourse, hasCertificate: e.target.checked})}
                    className="w-4 h-4 text-primary rounded focus:ring-primary"
                  />
                  <label htmlFor="hasCertificate" className="ml-2 text-sm text-gray-700">
                    Includes certificate
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                <button
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCourse}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  Create Course
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Courses Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {sortedCourses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8">
                    <input
                      type="checkbox"
                      checked={selectedCourses.length === sortedCourses.length && sortedCourses.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-primary rounded focus:ring-primary"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stats</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedCourses.includes(course.id)}
                        onChange={() => handleSelectCourse(course.id)}
                        className="w-4 h-4 text-primary rounded focus:ring-primary"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {course.thumbnail_url || course.image ? (
                          <img 
                            src={course.thumbnail_url || course.image} 
                            alt={course.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">{course.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">{course.description}</div>
                          <div className="flex items-center space-x-2 mt-1">
                            <div className="flex items-center text-xs text-gray-500">
                              <Target className="w-3 h-3 mr-1" />
                              <span>{getTotalLessons(course)} lessons</span>
                            </div>
                            <div className="flex items-center text-xs text-gray-500">
                              <Clock className="w-3 h-3 mr-1" />
                              <span>{course.duration}</span>
                            </div>
                            {course.hasCertificate && (
                              <div className="flex items-center text-xs text-green-600">
                                <Award className="w-3 h-3 mr-1" />
                                <span>Certificate</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {course.category || 'Uncategorized'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getLevelColor(course.skillLevel)}`}>
                        {course.skillLevel || 'Not Set'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getCourseTierColor(course.tier)}`}>
                        {mapCourseTierForDisplay(course.tier)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Users className="w-3 h-3" />
                          <span>{course.students?.toLocaleString() || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-current" />
                          <span>{course.rating || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Zap className="w-3 h-3" />
                          <span>{course.module_count || 0} modules</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        !course.is_published ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {!course.is_published ? 'Draft' : 'Published'}
                      </span>
                    </td>
                    <td className="px-6 py-4 space-x-2">
                      <button 
                        onClick={() => handleEditCourse(course)}
                        className="text-primary hover:text-primary/80 p-1"
                        title="Edit course"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleTogglePublish(course)}
                        className="text-gray-600 hover:text-gray-800 p-1"
                        title={!course.is_published ? 'Publish course' : 'Unpublish course'}
                      >
                        {!course.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button 
                        onClick={() => handleDeleteCourse(course)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete course"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">
              {courses?.length === 0 ? 'No courses found' : 'No matching courses'}
            </p>
            <p className="text-sm mt-1">
              {searchTerm 
                ? `No results for "${searchTerm}"`
                : 'Try adjusting your filters or create your first course'
              }
            </p>
            {courses?.length === 0 && (
              <button 
                onClick={() => setIsCreating(true)}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Create First Course
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// PodcastManagementTab Component
function PodcastManagementTab({ podcasts, loading, onRefresh, categories }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterTopic, setFilterTopic] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedPodcasts, setSelectedPodcasts] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newPodcast, setNewPodcast] = useState({
    title: '',
    summary: '',
    topic: '',
    difficulty: 'Beginner',
    duration: '',
    publish_date: new Date().toISOString().split('T')[0],
    audio_url: '',
    thumbnail_url: '',
    tier: 'free',
    episode_type: 'Deep Dive',
    topics: [],
    full_script: '',
    show_notes: [],
    listens: 0,
    is_downloadable: true,
    related_course_id: null,
    is_published: false
  });

  // Filter and sort podcasts
  const filteredPodcasts = podcasts?.filter(podcast => {
    const matchesSearch = podcast.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         podcast.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         podcast.topic?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTier = filterTier === 'all' || podcast.tier === filterTier;
    const matchesTopic = filterTopic === 'all' || podcast.topic === filterTopic;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'published' && podcast.is_published) ||
                         (filterStatus === 'draft' && !podcast.is_published);
    const matchesType = filterType === 'all' || podcast.episode_type === filterType;

    return matchesSearch && matchesTier && matchesTopic && matchesStatus && matchesType;
  }) || [];

  // Sort podcasts
  const sortedPodcasts = [...filteredPodcasts].sort((a, b) => {
    switch(sortBy) {
      case 'newest':
        return new Date(b.publish_date || b.created_at || 0) - new Date(a.publish_date || a.created_at || 0);
      case 'oldest':
        return new Date(a.publish_date || a.created_at || 0) - new Date(b.publish_date || b.created_at || 0);
      case 'listens_high':
        return (b.listens || 0) - (a.listens || 0);
      case 'listens_low':
        return (a.listens || 0) - (b.listens || 0);
      case 'title_asc':
        return (a.title || '').localeCompare(b.title || '');
      case 'title_desc':
        return (b.title || '').localeCompare(a.title || '');
      default:
        return 0;
    }
  });

  // Bulk actions
  const handleSelectAll = () => {
    if (selectedPodcasts.length === sortedPodcasts.length) {
      setSelectedPodcasts([]);
    } else {
      setSelectedPodcasts(sortedPodcasts.map(p => p.id));
    }
  };

  const handleSelectPodcast = (podcastId) => {
    setSelectedPodcasts(prev => 
      prev.includes(podcastId) 
        ? prev.filter(id => id !== podcastId)
        : [...prev, podcastId]
    );
  };

  const handleBulkActions = (action) => {
    if (selectedPodcasts.length === 0) return;
    const actionText = action === 'publish' ? 'Publish' : 
                      action === 'unpublish' ? 'Unpublish' : 'Delete';
    const confirmMsg = `${actionText} ${selectedPodcasts.length} selected podcasts?`;
    if (confirm(confirmMsg)) {
      alert(`${actionText} ${selectedPodcasts.length} podcasts - To be implemented with backend API`);
      setSelectedPodcasts([]);
    }
  };

  // Podcast actions
  const handleCreatePodcast = async () => {
    if (!newPodcast.title || !newPodcast.summary) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      alert('Podcast created successfully! (API integration needed)');
      setIsCreating(false);
      setNewPodcast({
        title: '',
        summary: '',
        topic: '',
        difficulty: 'Beginner',
        duration: '',
        publish_date: new Date().toISOString().split('T')[0],
        audio_url: '',
        thumbnail_url: '',
        tier: 'free',
        episode_type: 'Deep Dive',
        topics: [],
        full_script: '',
        show_notes: [],
        listens: 0,
        is_downloadable: true,
        related_course_id: null,
        is_published: false
      });
      onRefresh();
    } catch (error) {
      alert('Failed to create podcast: ' + error.message);
    }
  };

  const handleEditPodcast = (podcast) => {
    alert(`Edit podcast: ${podcast.title} - Backend API endpoint needed`);
  };

  const handleDeletePodcast = (podcast) => {
    if (confirm(`Are you sure you want to delete "${podcast.title}"? This action cannot be undone.`)) {
      alert(`Delete podcast: ${podcast.title} - Backend API endpoint needed`);
    }
  };

  const handleTogglePublish = (podcast) => {
    const action = !podcast.is_published ? 'publish' : 'unpublish';
    if (confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} podcast "${podcast.title}"?`)) {
      alert(`${action} podcast: ${podcast.title} - Backend API endpoint needed`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading podcasts...</span>
      </div>
    );
  }

  // Get episode types from podcasts
  const episodeTypes = [...new Set(podcasts.map(p => p.episode_type).filter(Boolean))];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Podcast Management</h3>
          <p className="text-sm text-gray-600">
            {podcasts?.length || 0} total podcasts • {filteredPodcasts.length} filtered
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={onRefresh}
            className="p-2 rounded-lg hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setIsCreating(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            <span>Add Podcast</span>
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedPodcasts.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">
                {selectedPodcasts.length} podcast{selectedPodcasts.length > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkActions('publish')}
                className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
              >
                Publish
              </button>
              <button
                onClick={() => handleBulkActions('unpublish')}
                className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-sm"
              >
                Unpublish
              </button>
              <button
                onClick={() => handleBulkActions('delete')}
                className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedPodcasts([])}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search podcasts by title, summary, or topic..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Tiers</option>
              <option value="free">Free</option>
              <option value="plus">Plus</option>
              <option value="pro">Pro</option>
            </select>
            
            <select
              value={filterTopic}
              onChange={(e) => setFilterTopic(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Topics</option>
              {categories?.podcast_topics?.map(topic => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </select>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="all">All Types</option>
              {episodeTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="listens_high">Most Listens</option>
              <option value="listens_low">Fewest Listens</option>
              <option value="title_asc">Title A-Z</option>
              <option value="title_desc">Title Z-A</option>
            </select>
          </div>
        </div>
      </div>

      {/* Create Podcast Modal */}
      {isCreating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Create New Podcast</h3>
                <button 
                  onClick={() => setIsCreating(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Podcast Title *
                  </label>
                  <input
                    type="text"
                    value={newPodcast.title}
                    onChange={(e) => setNewPodcast({...newPodcast, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter podcast title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Summary *
                  </label>
                  <textarea
                    value={newPodcast.summary}
                    onChange={(e) => setNewPodcast({...newPodcast, summary: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter podcast summary"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Topic
                    </label>
                    <input
                      type="text"
                      value={newPodcast.topic}
                      onChange={(e) => setNewPodcast({...newPodcast, topic: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="e.g., Technology & AI"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={newPodcast.duration}
                      onChange={(e) => setNewPodcast({...newPodcast, duration: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="e.g., 15:00"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tier
                    </label>
                    <select
                      value={newPodcast.tier}
                      onChange={(e) => setNewPodcast({...newPodcast, tier: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="free">Free</option>
                      <option value="plus">Plus</option>
                      <option value="pro">Pro</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Episode Type
                    </label>
                    <select
                      value={newPodcast.episode_type}
                      onChange={(e) => setNewPodcast({...newPodcast, episode_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="Deep Dive">Deep Dive</option>
                      <option value="Tutorial">Tutorial</option>
                      <option value="Critique">Critique</option>
                      <option value="Educational">Educational</option>
                      <option value="Brief">Brief</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Publish Date
                    </label>
                    <input
                      type="date"
                      value={newPodcast.publish_date}
                      onChange={(e) => setNewPodcast({...newPodcast, publish_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Audio URL
                    </label>
                    <input
                      type="text"
                      value={newPodcast.audio_url}
                      onChange={(e) => setNewPodcast({...newPodcast, audio_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="/audio/podcasts/..."
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thumbnail URL
                  </label>
                  <input
                    type="text"
                    value={newPodcast.thumbnail_url}
                    onChange={(e) => setNewPodcast({...newPodcast, thumbnail_url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="https://images.unsplash.com/photo-..."
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_downloadable"
                    checked={newPodcast.is_downloadable}
                    onChange={(e) => setNewPodcast({...newPodcast, is_downloadable: e.target.checked})}
                    className="w-4 h-4 text-primary rounded focus:ring-primary"
                  />
                  <label htmlFor="is_downloadable" className="ml-2 text-sm text-gray-700">
                    Allow downloads
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
                <button
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePodcast}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  Create Podcast
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Podcasts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {sortedPodcasts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8">
                    <input
                      type="checkbox"
                      checked={selectedPodcasts.length === sortedPodcasts.length && sortedPodcasts.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-primary rounded focus:ring-primary"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Podcast</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stats</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedPodcasts.map((podcast) => (
                  <tr key={podcast.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedPodcasts.includes(podcast.id)}
                        onChange={() => handleSelectPodcast(podcast.id)}
                        className="w-4 h-4 text-primary rounded focus:ring-primary"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {podcast.thumbnail_url || podcast.image ? (
                          <img 
                            src={podcast.thumbnail_url || podcast.image} 
                            alt={podcast.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                            <Headphones className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">{podcast.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">{podcast.summary}</div>
                          <div className="flex items-center space-x-2 mt-1">
                            <div className="flex items-center text-xs text-gray-500">
                              <Clock className="w-3 h-3 mr-1" />
                              <span>{podcast.duration}</span>
                            </div>
                            {podcast.is_downloadable && (
                              <div className="flex items-center text-xs text-green-600">
                                <Download className="w-3 h-3 mr-1" />
                                <span>Downloadable</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {podcast.topic || 'Uncategorized'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {podcast.episode_type || 'Deep Dive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPodcastTierColor(podcast.tier)}`}>
                        {podcast.tier?.charAt(0).toUpperCase() + podcast.tier?.slice(1) || 'Free'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Headphones className="w-3 h-3" />
                          <span>{podcast.listens?.toLocaleString() || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{podcast.publish_date || 'No date'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        !podcast.is_published ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {!podcast.is_published ? 'Draft' : 'Published'}
                      </span>
                    </td>
                    <td className="px-6 py-4 space-x-2">
                      <button 
                        onClick={() => handleEditPodcast(podcast)}
                        className="text-primary hover:text-primary/80 p-1"
                        title="Edit podcast"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleTogglePublish(podcast)}
                        className="text-gray-600 hover:text-gray-800 p-1"
                        title={!podcast.is_published ? 'Publish podcast' : 'Unpublish podcast'}
                      >
                        {!podcast.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button 
                        onClick={() => handleDeletePodcast(podcast)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete podcast"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Headphones className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">
              {podcasts?.length === 0 ? 'No podcasts found' : 'No matching podcasts'}
            </p>
            <p className="text-sm mt-1">
              {searchTerm 
                ? `No results for "${searchTerm}"`
                : 'Try adjusting your filters or create your first podcast'
              }
            </p>
            {podcasts?.length === 0 && (
              <button 
                onClick={() => setIsCreating(true)}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Create First Podcast
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// BulkImportTab Component
function BulkImportTab({ token, onImportComplete }) {
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importType, setImportType] = useState('courses');

  const handleBulkImport = async () => {
    setImporting(true);
    setImportStatus(null);

    try {
      const data = importType === 'courses' ? mockCourses : mockPodcasts;
      const endpoint = importType === 'courses' 
        ? adminApi.bulkImportCourses 
        : adminApi.bulkImportPodcasts;

      const result = await endpoint(data, token);
      
      setImportStatus({
        success: result.success,
        message: result.message,
        imported: result.imported?.length || 0,
        errors: result.errors?.length || 0,
        details: result
      });

      if (result.success && onImportComplete) {
        setTimeout(() => {
          onImportComplete();
        }, 1000);
      }
    } catch (error) {
      setImportStatus({
        success: false,
        message: `Failed to import ${importType}: ${error.message}`,
        imported: 0,
        errors: 0
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Bulk Import</h3>
        <p className="text-sm text-gray-600">
          Import your mock data into the database
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Import Type
            </label>
            <select
              value={importType}
              onChange={(e) => setImportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={importing}
            >
              <option value="courses">Courses ({mockCourses.length} items)</option>
              <option value="podcasts">Podcasts ({mockPodcasts.length} items)</option>
            </select>
          </div>

          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium">Before importing:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Make sure you have a backup of your database</li>
                  <li>This will insert {importType === 'courses' ? mockCourses.length : mockPodcasts.length} records</li>
                  <li>Existing data will not be modified or deleted</li>
                  <li>Courses will be imported with their modules and lessons</li>
                </ul>
              </div>
            </div>
          </div>

          <button
            onClick={handleBulkImport}
            disabled={importing}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium ${
              importing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-white'
            }`}
          >
            {importing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Importing {importType}...</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>Import {importType === 'courses' ? mockCourses.length : mockPodcasts.length} {importType}</span>
              </>
            )}
          </button>

          {importStatus && (
            <div className={`p-4 rounded-lg border ${
              importStatus.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start">
                {importStatus.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-medium ${
                    importStatus.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {importStatus.message}
                  </p>
                  {importStatus.imported > 0 && (
                    <p className="text-sm mt-1 text-gray-600">
                      Successfully imported {importStatus.imported} {importType}
                      {importStatus.errors > 0 && ` with ${importStatus.errors} errors`}
                    </p>
                  )}
                  {importStatus.details?.imported && (
                    <div className="mt-2 text-sm">
                      <p className="font-medium">Imported items:</p>
                      <ul className="mt-1 space-y-1">
                        {importStatus.details.imported.slice(0, 5).map((item, index) => (
                          <li key={index} className="flex items-center">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                            <span>{item.title} (ID: {item.id})</span>
                          </li>
                        ))}
                        {importStatus.details.imported.length > 5 && (
                          <li className="text-gray-500">
                            ...and {importStatus.details.imported.length - 5} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {importType === 'courses' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="font-medium text-gray-900">Courses to Import</h4>
            <p className="text-sm text-gray-600">Preview of courses that will be imported</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modules</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lessons</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mockCourses.slice(0, 5).map((course) => (
                  <tr key={course.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{course.title}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        course.tier === 'pro' || course.tier === 'scholarship' ? 'bg-purple-100 text-purple-800' :
                        course.tier === 'premium' || course.tier === 'plus' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {course.tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{course.modules?.length || 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {course.modules?.reduce((total, mod) => total + (mod.lessons?.length || 0), 0) || 0}
                    </td>
                  </tr>
                ))}
                {mockCourses.length > 5 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                      ...and {mockCourses.length - 5} more courses
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importType === 'podcasts' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="font-medium text-gray-900">Podcasts to Import</h4>
            <p className="text-sm text-gray-600">Preview of podcasts that will be imported</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Listens</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mockPodcasts.slice(0, 5).map((podcast) => (
                  <tr key={podcast.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate max-w-xs">{podcast.title}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        podcast.tier === 'pro' ? 'bg-purple-100 text-purple-800' :
                        podcast.tier === 'plus' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {podcast.tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{podcast.duration}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{podcast.listens?.toLocaleString() || 0}</td>
                  </tr>
                ))}
                {mockPodcasts.length > 5 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                      ...and {mockPodcasts.length - 5} more podcasts
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// SystemHealthTab Component
function SystemHealthTab({ databaseStatus, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">System Health</h3>
          <p className="text-sm text-gray-600">Monitor system status and database connections</p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Database Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="font-medium text-gray-900">Database Connections</h4>
            <p className="text-sm text-gray-600">Status of all database connections</p>
          </div>
          <DatabaseIcon className="w-5 h-5 text-gray-400" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {databaseStatus ? (
            <>
              <DatabaseStatusCard 
                name="saints_admin" 
                status={databaseStatus.saints_admin} 
              />
              <DatabaseStatusCard 
                name="saints_content" 
                status={databaseStatus.saints_content} 
              />
              <DatabaseStatusCard 
                name="users_login" 
                status={databaseStatus.users_login} 
              />
            </>
          ) : (
            <div className="col-span-3 flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-gray-600">Loading database status...</span>
            </div>
          )}
        </div>
      </div>

      {/* System Metrics */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-6">System Metrics</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">CPU Usage</span>
              <Cpu className="w-4 h-4 text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900">24%</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div className="bg-green-500 h-2 rounded-full" style={{ width: '24%' }}></div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Memory Usage</span>
              <Server className="w-4 h-4 text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900">1.2GB / 4GB</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div className="bg-blue-500 h-2 rounded-full" style={{ width: '30%' }}></div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Disk Usage</span>
              <HardDrive className="w-4 h-4 text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900">45%</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '45%' }}></div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Uptime</span>
              <Activity className="w-4 h-4 text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900">15d 6h</div>
            <div className="text-sm text-gray-600 mt-1">99.9% availability</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-6">Recent Activity</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Course imported</p>
                <p className="text-xs text-gray-500">5 minutes ago</p>
              </div>
            </div>
            <span className="text-sm text-gray-600">Introduction to AI and Machine Learning</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Edit className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Podcast updated</p>
                <p className="text-xs text-gray-500">1 hour ago</p>
              </div>
            </div>
            <span className="text-sm text-gray-600">Digital Marketing Mastery</span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">User registered</p>
                <p className="text-xs text-gray-500">2 hours ago</p>
              </div>
            </div>
            <span className="text-sm text-gray-600">New Pro user</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// EmailManagementTab Component
function EmailManagementTab() {
  const [emailStats, setEmailStats] = useState([]);
  const [recentEmails, setRecentEmails] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('stats');
  const [selectedType, setSelectedType] = useState('all');

  const adminToken = localStorage.getItem('adminToken');
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const API_BASE = isLocal ? '${API_BASE}' : '/api/v2/admin';

  const fetchEmailData = async () => {
    setLoading(true);
    try {
      // Fetch email stats
      const statsRes = await fetch(`${API_BASE}/email-api.php?action=get-email-stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const statsData = await statsRes.json();
      console.log('Email stats response:', statsData);
      
      if (statsData.success) {
        setEmailStats(statsData.stats || statsData.data || []);
      }

      // Fetch recent emails
      const emailsRes = await fetch(`${API_BASE}/email-api.php?action=get-recent-emails&limit=50`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const emailsData = await emailsRes.json();
      console.log('Recent emails response:', emailsData);
      
      if (emailsData.success) {
        setRecentEmails(emailsData.data || emailsData.notifications || []);
      }

      // Fetch subscribers
      const subsRes = await fetch(`${API_BASE}/email-api.php?action=get-subscribers${selectedType !== 'all' ? `&type=${selectedType}` : ''}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const subsData = await subsRes.json();
      console.log('Subscribers response:', subsData);
      
      if (subsData.success) {
        setSubscribers(subsData.data || subsData.subscribers || []);
      }

    } catch (error) {
      console.error('Failed to fetch email data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailData();
  }, [selectedType]);

  const handleResendEmail = async (emailId) => {
    if (!confirm('Resend this email?')) return;
    
    try {
      const email = recentEmails.find(e => e.id === emailId);
      if (!email) return;
      
      const response = await fetch(`${API_BASE}/email-api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          action: 'send-welcome',
          email: email.email,
          name: email.name,
          id: email.user_id
        })
      });
      
      const result = await response.json();
      if (result.success) {
        alert('Email resent successfully!');
        fetchEmailData();
      } else {
        alert(`Failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Resend error:', error);
      alert('Failed to resend email');
    }
  };

  const handleSendTestEmail = async () => {
    const email = prompt('Enter email address for test:');
    if (!email) return;
    
    try {
      const response = await fetch(`${API_BASE}/email-api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          action: 'send-welcome',
          email: email,
          name: 'Test User'
        })
      });
      
      const result = await response.json();
      if (result.success) {
        alert('Test email sent successfully!');
        fetchEmailData();
      } else {
        alert(`Failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Test email error:', error);
      alert('Failed to send test email');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading email data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Email Management</h3>
          <p className="text-sm text-gray-600">Manage email notifications and view analytics</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={fetchEmailData}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button 
            onClick={handleSendTestEmail}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            <Mail className="w-4 h-4" />
            <span>Send Test Email</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveView('stats')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeView === 'stats' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Statistics
          </button>
          <button
            onClick={() => setActiveView('emails')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeView === 'emails' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Email Logs ({recentEmails.length})
          </button>
          <button
            onClick={() => setActiveView('subscribers')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeView === 'subscribers' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Subscribers ({subscribers.length})
          </button>
        </nav>
      </div>

      {/* Stats View */}
      {activeView === 'stats' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Total Emails</h4>
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                  <Mail className="w-5 h-5" />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {emailStats.reduce((sum, stat) => sum + parseInt(stat.total), 0)}
              </div>
              <p className="text-sm text-gray-600">All email notifications</p>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Successful</h4>
                <div className="p-2 rounded-lg bg-green-100 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {emailStats.reduce((sum, stat) => sum + parseInt(stat.sent), 0)}
              </div>
              <p className="text-sm text-gray-600">Emails delivered</p>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Failed</h4>
                <div className="p-2 rounded-lg bg-red-100 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {emailStats.reduce((sum, stat) => sum + parseInt(stat.failed), 0)}
              </div>
              <p className="text-sm text-gray-600">Delivery failed</p>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Pending</h4>
                <div className="p-2 rounded-lg bg-yellow-100 text-yellow-600">
                  <Clock className="w-5 h-5" />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {emailStats.reduce((sum, stat) => sum + parseInt(stat.pending), 0)}
              </div>
              <p className="text-sm text-gray-600">Awaiting delivery</p>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">Email Statistics by Type</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {emailStats.map((stat, index) => {
                    const successRate = stat.total > 0 ? ((parseInt(stat.sent) / parseInt(stat.total)) * 100).toFixed(1) : 0;
                    return (
                      <tr key={index}>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                            {stat.notification_type?.replace('_', ' ') || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4">{stat.total}</td>
                        <td className="px-6 py-4">
                          <span className="text-green-600 font-medium">{stat.sent}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-red-600 font-medium">{stat.failed}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-yellow-600 font-medium">{stat.pending}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-500 h-2 rounded-full" 
                                style={{ width: `${successRate}%` }}
                              ></div>
                            </div>
                            <span className="ml-2 text-sm text-gray-600">{successRate}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Email Logs View */}
      {activeView === 'emails' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h4 className="font-medium text-gray-900">Recent Email Notifications</h4>
            <div className="flex items-center space-x-2">
              <select 
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="all">All Types</option>
                <option value="welcome">Welcome</option>
                <option value="podcast_available">Podcast</option>
                <option value="saints_plus">Saints Plus</option>
                <option value="courses_available">Courses</option>
                <option value="business_tier_inquiry">Business</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentEmails
                  .filter(email => selectedType === 'all' || email.notification_type === selectedType)
                  .map((email) => (
                    <tr key={email.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{email.email}</div>
                          {email.user_name && (
                            <div className="text-sm text-gray-500">{email.user_name}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          email.notification_type === 'welcome' ? 'bg-blue-100 text-blue-800' :
                          email.notification_type === 'podcast_available' ? 'bg-green-100 text-green-800' :
                          email.notification_type === 'saints_plus' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {email.notification_type?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          email.status === 'sent' ? 'bg-green-100 text-green-800' :
                          email.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {email.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(email.created_at).toLocaleDateString()}
                        <div className="text-xs text-gray-500">
                          {new Date(email.created_at).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {email.status === 'failed' && (
                          <button
                            onClick={() => handleResendEmail(email.id)}
                            className="text-primary hover:text-primary/80 text-sm"
                          >
                            Resend
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Subscribers View */}
      {activeView === 'subscribers' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h4 className="font-medium text-gray-900">Notification Subscribers</h4>
            <div className="flex items-center space-x-2">
              <select 
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="all">All Subscriptions</option>
                <option value="plus_access">Saints Plus</option>
                <option value="pro_access">Saints Pro</option>
                <option value="course_launch">Courses</option>
                <option value="podcast_notification">Podcasts</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscribed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscribers
                  .filter(sub => selectedType === 'all' || sub.type === selectedType)
                  .map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{subscriber.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        {subscriber.name || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                          {subscriber.type?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          subscriber.tier === 'pro' ? 'bg-purple-100 text-purple-800' :
                          subscriber.tier === 'plus' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {subscriber.tier || 'free'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {subscriber.source}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(subscriber.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// AmbassadorManagementTab Component
function AmbassadorManagementTab() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    reviewed: 0,
    total: 0
  });
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showReviewModal, setShowReviewModal] = useState(false);

  const adminToken = localStorage.getItem('adminToken');
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const API_BASE = isLocal ? '${API_BASE}' : '/api/v2/admin';

  const fetchApplications = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/ambassador-applications.php?action=get-applications`;
      if (activeFilter !== 'all') {
        url += `&status=${activeFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      console.log('Ambassador applications response:', data);
      
      if (data.success) {
        setApplications(data.data || []);
      } else {
        console.error('Failed to fetch applications:', data.error);
      }
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/ambassador-applications.php?action=get-stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      console.log('Ambassador stats response:', data);
      
      if (data.success) {
        setStats({
          pending: data.data.pending || 0,
          approved: data.data.approved || 0,
          rejected: data.data.rejected || 0,
          reviewed: data.data.reviewed || 0,
          total: data.total || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchApplications();
    fetchStats();
  }, [activeFilter]);

  const handleStatusUpdate = async (applicationId, status) => {
    try {
      const application = applications.find(app => app.id === applicationId);
      if (!application) {
        alert('Application not found');
        return;
      }

      const response = await fetch(`${API_BASE}/ambassador-applications.php?action=update&id=${applicationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          status: status,
          review_notes: reviewNotes || ''
        })
      });
      
      const data = await response.json();
      console.log('Update response:', data);
      
      if (data.success) {
        setShowReviewModal(false);
        setReviewNotes('');
        setSelectedApplication(null);
        
        // Refresh both applications and stats
        await fetchApplications();
        await fetchStats();
        
        // Send email notification
        await sendStatusEmail(application, status);
        
        alert(`Application ${status} successfully!`);
      } else {
        alert(`Failed to update status: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update application status');
    }
  };

  const sendStatusEmail = async (application, status) => {
    try {
      if (!application) return;
      
      await fetch(`${API_BASE}/email-api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          action: 'send-ambassador-status',
          email: application.email,
          name: application.name,
          application_id: application.id,
          status: status,
          notes: reviewNotes
        })
      });
    } catch (error) {
      console.error('Email error:', error);
    }
  };

  const openReviewModal = (application) => {
    setSelectedApplication(application);
    setReviewNotes(application.review_notes || '');
    setShowReviewModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">Loading ambassador applications...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Ambassador Program Management</h3>
          <p className="text-sm text-gray-600">Review and manage ambassador applications</p>
        </div>
        <button 
          onClick={() => { fetchApplications(); fetchStats(); }}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-500">Total Applications</h4>
            <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
              <Award className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">{stats.total}</div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-500">Pending Review</h4>
            <div className="p-2 rounded-lg bg-yellow-100 text-yellow-600">
              <Clock className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">{stats.pending}</div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-500">Approved</h4>
            <div className="p-2 rounded-lg bg-green-100 text-green-600">
              <CheckCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">{stats.approved}</div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-500">Reviewed</h4>
            <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
              <Eye className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">{stats.reviewed}</div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-500">Rejected</h4>
            <div className="p-2 rounded-lg bg-red-100 text-red-600">
              <AlertCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">{stats.rejected}</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4">
          <button
            onClick={() => setActiveFilter('all')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${activeFilter === 'all' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            All Applications ({stats.total})
          </button>
          <button
            onClick={() => setActiveFilter('pending')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${activeFilter === 'pending' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Pending ({stats.pending})
          </button>
          <button
            onClick={() => setActiveFilter('approved')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${activeFilter === 'approved' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Approved ({stats.approved})
          </button>
          <button
            onClick={() => setActiveFilter('rejected')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${activeFilter === 'rejected' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Rejected ({stats.rejected})
          </button>
        </nav>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {applications.map((application) => (
                <tr key={application.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{application.name}</div>
                      <div className="text-sm text-gray-500">{application.email}</div>
                      <div className="text-xs text-gray-400">ID: {application.id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      application.application_type === 'social' ? 'bg-blue-100 text-blue-800' :
                      application.application_type === 'financial' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {application.application_type || 'Not specified'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(application.applied_at || application.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      application.status === 'approved' ? 'bg-green-100 text-green-800' :
                      application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {application.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openReviewModal(application)}
                        className="text-primary hover:text-primary/80 text-sm px-3 py-1 border border-primary rounded hover:bg-primary/5"
                      >
                        Review
                      </button>
                      {application.status === 'pending' && (
                        <>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to approve this application?')) {
                                handleStatusUpdate(application.id, 'approved');
                              }
                            }}
                            className="text-green-600 hover:text-green-800 text-sm px-3 py-1 border border-green-600 rounded hover:bg-green-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to reject this application?')) {
                                handleStatusUpdate(application.id, 'rejected');
                              }
                            }}
                            className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-600 rounded hover:bg-red-50"
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {applications.length === 0 && (
            <div className="text-center py-12">
              <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No applications found</p>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Review Application</h3>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Applicant Information</h4>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <div className="text-sm text-gray-500">Name</div>
                      <div className="font-medium">{selectedApplication.name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Email</div>
                      <div className="font-medium">{selectedApplication.email}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Applied</div>
                      <div className="font-medium">{formatDate(selectedApplication.applied_at || selectedApplication.created_at)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">ID</div>
                      <div className="font-medium">{selectedApplication.id}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Application Type</h4>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full ${
                      selectedApplication.application_type === 'social' ? 'bg-blue-100 text-blue-800' :
                      selectedApplication.application_type === 'financial' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedApplication.application_type || 'Not specified'}
                    </span>
                    {selectedApplication.application_type === 'social' && (
                      <div className="text-gray-600">
                        {selectedApplication.social_platform}: @{selectedApplication.social_handle} 
                        {selectedApplication.followers && ` (${selectedApplication.followers} followers)`}
                      </div>
                    )}
                  </div>
                </div>
                
                {selectedApplication.application_type === 'financial' && selectedApplication.financial_situation && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Financial Situation</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-line">{selectedApplication.financial_situation}</p>
                    </div>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Motivation</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-line">{selectedApplication.motivation}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review Notes
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Add notes about this application..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setShowReviewModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to reject this application?')) {
                        handleStatusUpdate(selectedApplication.id, 'rejected');
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Reject Application
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to approve this application?')) {
                        handleStatusUpdate(selectedApplication.id, 'approved');
                      }
                    }}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                  >
                    Approve Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default AdminDashboard;